import { Symbols } from "@tenforce/toolbox-fontmap"

declare let window: OldUIContext.TenForceWindow

const enum Languages {
    enGB = "en-GB",
    enUS = "en-US", // As far as I know, there is no distinction between US and UK English in app. To investigate.
    nlBE = "nl-BE",
    frFR = "fr-FR",
    deDE = "de-DE",
}
const defaultLanguage = Languages.enGB
const userLanguage = (window.env.language as Languages) || defaultLanguage

interface RawTerm {
    code: string
    target?: string
    translations: Record<string, TermTranslation>
}

interface TermTranslation {
    content: string
    label: string
}

export const isContextualHelpAvailableForUser = (): boolean =>
    [
        Languages.deDE,
        Languages.enGB,
        Languages.enUS,
        Languages.frFR,
        Languages.nlBE,
    ].contains(userLanguage)

const rawTerms: RawTerm[] = [
    {
        code: "NavigationMenu",
        target: "#showNavigationMenuButton",
        translations: {
            [Languages.enGB]: {
                label: `Navigation panel ${Symbols.SubMenu}`,
                content:
                    "Get an overview of all workspaces and their content, your bookmarks and shortcuts, and recommended collections.",
            },
            [Languages.enUS]: {
                label: `Navigation panel ${Symbols.SubMenu}`,
                content:
                    "Get an overview of all workspaces and their content, your bookmarks and shortcuts, and recommended collections.",
            },
            [Languages.nlBE]: {
                label: `Navigatiepaneel ${Symbols.SubMenu}`,
                content:
                    "Een overzicht van alle werkruimten en hun inhoud, uw bladwijzers en snelkoppelingen, en aanbevolen collecties.",
            },
            [Languages.frFR]: {
                label: `Panneau de navigation ${Symbols.SubMenu}`,
                content:
                    "Obtenir une vue d'ensemble de tous les espaces de travail et de leur contenu, de vos favoris et raccourcis, ainsi que des collections recommandées.",
            },
            [Languages.deDE]: {
                label: `Navigations-Panel ${Symbols.SubMenu}`,
                content:
                    "Verschaffen Sie sich einen Überblick über alle Arbeitsbereiche und deren Inhalt, Ihre Lesezeichen und Verknüpfungen sowie über empfohlene Kollektionen.",
            },
        },
    },
    {
        code: "SmartLocation",
        target: "#smartLocationContainer [data-ft-breadcrumb-type='relative']:not(:first-child)",
        translations: {
            [Languages.enGB]: {
                label: "Smart location",
                content:
                    "Learn where in the system you are or how you got there.",
            },
            [Languages.enUS]: {
                label: "Smart location",
                content:
                    "Learn where in the system you are or how you got there.",
            },
            [Languages.nlBE]: {
                label: "Slimme locatie",
                content:
                    "Leer waar in het systeem u bent of hoe u er bent gekomen.",
            },
            [Languages.frFR]: {
                label: "Localisation intelligente",
                content:
                    "Apprendre où vous vous situez dans le système ou comment vous y êtes arrivé.",
            },
            [Languages.deDE]: {
                label: "Intelligenter Standort",
                content:
                    "Erfahren Sie, wo Sie sich im System befinden oder wie Sie dorthin gelangt sind.",
            },
        },
    },
    {
        code: "WelcomePage",
        target: "#smartLocationContainer [data-ft-breadcrumb-type='relative']:first-child",
        translations: {
            [Languages.enGB]: {
                label: `Welcome page ${Symbols.TenForceLogo}`,
                content:
                    "A navigation launch point. Access your bookmarks and shortcuts and available workspaces or search for items.",
            },
            [Languages.enUS]: {
                label: `Welcome page ${Symbols.TenForceLogo}`,
                content:
                    "A navigation launch point. Access your bookmarks and shortcuts and available workspaces or search for items.",
            },
            [Languages.nlBE]: {
                label: `Welkomstpagina ${Symbols.TenForceLogo}`,
                content:
                    "Een navigatiestartpunt. Toegang tot uw bladwijzers en snelkoppelingen en beschikbare werkruimten of zoeken naar items.",
            },
            [Languages.frFR]: {
                label: `Page de bienvenue ${Symbols.TenForceLogo}`,
                content:
                    "Point de départ de la navigation. Accéder à vos signets et raccourcis, aux espaces de travail disponibles ou rechercher des éléments.",
            },
            [Languages.deDE]: {
                label: `Willkommensseite ${Symbols.TenForceLogo}`,
                content:
                    "Ein Startpunkt für die Navigation. Greifen Sie auf Ihre Lesezeichen und Verknüpfungen sowie die verfügbaren Arbeitsbereiche zu oder suchen Sie nach Elementen.",
            },
        },
    },
    {
        code: "LateralNavigationRelativeSiblings",
        target: "#relativeSiblingsButton",
        translations: {
            [Languages.enGB]: {
                label: `Lateral navigation ${Symbols.Siblings}`,
                content: "Quickly navigate between pages of the workspace.",
            },
            [Languages.enUS]: {
                label: `Lateral navigation ${Symbols.Siblings}`,
                content: "Quickly navigate between pages of the workspace.",
            },
            [Languages.nlBE]: {
                label: `Laterale navigatie ${Symbols.Siblings}`,
                content: "Navigeer snel tussen pagina's van de werkruimte.",
            },
            [Languages.frFR]: {
                label: `Navigation latérale ${Symbols.Siblings}`,
                content:
                    "Naviguer rapidement entre les pages de l'espace de travail.",
            },
            [Languages.deDE]: {
                label: `Seitliche Navigation ${Symbols.Siblings}`,
                content:
                    "Navigieren Sie schnell zwischen den Seiten des Arbeitsbereichs.",
            },
        },
    },
    {
        code: "CreateNewItems",
        target: "#createNewItemButton",
        translations: {
            [Languages.enGB]: {
                label: `Create new items ${Symbols.Create}`,
                content: "Create a new item of any concept.",
            },
            [Languages.enUS]: {
                label: `Create new items ${Symbols.Create}`,
                content: "Create a new item of any concept.",
            },
            [Languages.nlBE]: {
                label: `Nieuwe items aanmaken ${Symbols.Create}`,
                content: "Maak een nieuw item van een concept.",
            },
            [Languages.frFR]: {
                label: `Créer de nouveaux éléments ${Symbols.Create}`,
                content: "Créer un nouvel élément de n'importe quel concept.",
            },
            [Languages.deDE]: {
                label: `Neue Elemente erstellen ${Symbols.Create}`,
                content:
                    "Erstellen Sie ein neues Element eines beliebigen Konzepts.",
            },
        },
    },
    {
        code: "Search",
        target: "#showSearchButton, #searchInput",
        translations: {
            [Languages.enGB]: {
                label: `Search ${Symbols.Search}`,
                content: "Look for any item in any workspace.",
            },
            [Languages.enUS]: {
                label: `Search ${Symbols.Search}`,
                content: "Look for any item in any workspace.",
            },
            [Languages.nlBE]: {
                label: `Zoeken ${Symbols.Search}`,
                content: "Zoek naar items in elke werkruimte.",
            },
            [Languages.frFR]: {
                label: `Chercher ${Symbols.Search}`,
                content:
                    "Rechercher n'importe quel élément dans n'importe quel espace de travail.",
            },
            [Languages.deDE]: {
                label: `Suchen ${Symbols.Search}`,
                content:
                    "Suche nach einem beliebigen Element in einem beliebigen Arbeitsbereich.",
            },
        },
    },
    {
        code: "Notifications",
        target: "#pendingNotificationsButton",
        translations: {
            [Languages.enGB]: {
                label: `Notifications ${Symbols.Notifications}`,
                content:
                    "Get an overview of the items in the system requiring your attention. A red indicator denotes unseen notifications.",
            },
            [Languages.enUS]: {
                label: `Notifications ${Symbols.Notifications}`,
                content:
                    "Get an overview of the items in the system requiring your attention. A red indicator denotes unseen notifications.",
            },
            [Languages.nlBE]: {
                label: `Meldingen ${Symbols.Notifications}`,
                content:
                    "Krijg een overzicht van de items in het systeem die uw aandacht vragen. Een rode indicator duidt op ongeziene meldingen.",
            },
            [Languages.frFR]: {
                label: `Notifications ${Symbols.Notifications}`,
                content:
                    "Obtenir une vue d'ensemble des éléments du système qui requièrent votre attention. Un indicateur rouge signale les notifications qui n'ont pas été vues.",
            },
            [Languages.deDE]: {
                label: `Benachrichtigungen ${Symbols.Notifications}`,
                content:
                    "Verschaffen Sie sich einen Überblick über die Elemente im System, die Ihre Aufmerksamkeit erfordern. Ein roter Indikator kennzeichnet ungesehene Benachrichtigungen.",
            },
        },
    },
    {
        code: "UserMenu",
        target: "#showUserMenuButton",
        translations: {
            [Languages.enGB]: {
                label: `User menu ${Symbols.User}`,
                content: "Access your personal user area.",
            },
            [Languages.enUS]: {
                label: `User menu ${Symbols.User}`,
                content: "Access your personal user area.",
            },
            [Languages.nlBE]: {
                label: `Gebruikersmenu ${Symbols.User}`,
                content: "Toegang tot uw persoonlijke gebruikersruimte.",
            },
            [Languages.frFR]: {
                label: `Menu utilisateur ${Symbols.User}`,
                content: "Accéder à votre espace personnel.",
            },
            [Languages.deDE]: {
                label: `Benutzer-Menü ${Symbols.User}`,
                content: "Zugang zu Ihrem persönlichen Benutzerbereich.",
            },
        },
    },
    {
        code: "AttributesRibbon",
        target: ".NewUI-item-header .NewUI-item-id",
        translations: {
            [Languages.enGB]: {
                label: "Attribute ribbon",
                content:
                    "See the item's concept, its number (if available), and its hierarchy.",
            },
            [Languages.enUS]: {
                label: "Attribute ribbon",
                content:
                    "See the item's concept, its number (if available), and its hierarchy.",
            },
            [Languages.nlBE]: {
                label: "Attributenlint",
                content:
                    "Bekijk het concept van het item, het nummer (indien beschikbaar) en de hiërarchie.",
            },
            [Languages.frFR]: {
                label: "Ruban d'attributs",
                content:
                    "Voir le concept de l'article, son numéro (si disponible) et sa hiérarchie.",
            },
            [Languages.deDE]: {
                label: "Attribut-Farbband",
                content:
                    "Sehen Sie das Konzept des Elements, seine Nummer (falls vorhanden) und seine Hierarchie.",
            },
        },
    },
    {
        code: "FullHierarchy",
        target: "#hierarchyButton",
        translations: {
            [Languages.enGB]: {
                label: `Full hierarchy ${Symbols.HierarchicalView}`,
                content:
                    "See the full view of the item’s hierarchical location in the system.",
            },
            [Languages.enUS]: {
                label: `Full hierarchy ${Symbols.HierarchicalView}`,
                content:
                    "See the full view of the item’s hierarchical location in the system.",
            },
            [Languages.nlBE]: {
                label: `Volledige hiërarchie ${Symbols.HierarchicalView}`,
                content:
                    "Zie de volledige weergave van de hiërarchische locatie van het item in het systeem.",
            },
            [Languages.frFR]: {
                label: `Hiérarchie complète ${Symbols.HierarchicalView}`,
                content:
                    "Voir la vue complète de l'emplacement hiérarchique de l'élément dans le système.",
            },
            [Languages.deDE]: {
                label: `Vollständige Hierarchie ${Symbols.HierarchicalView}`,
                content:
                    "Zeigt die vollständige Ansicht der hierarchischen Position des Objekts im System an.",
            },
        },
    },
    {
        code: "Hierarchy",
        target: undefined,
        translations: {
            [Languages.enGB]: {
                label: "Hierarchy",
                content:
                    "See a simplified view of the item’s hierarchical location in the system.",
            },
            [Languages.nlBE]: {
                label: "Hiërarchie",
                content:
                    "Bekijk een vereenvoudigde weergave van de hiërarchische locatie van het item in het systeem.",
            },
            [Languages.frFR]: {
                label: "Hiérarchie",
                content:
                    "Voir une vue simplifiée de l'emplacement hiérarchique de l'élément dans le système.",
            },
            [Languages.deDE]: {
                label: "Hierarchie",
                content:
                    "Sie sehen eine vereinfachte Ansicht der hierarchischen Position des Artikels im System.",
            },
        },
    },
    {
        code: "PanelSwitch",
        target: "[data-ft-component='panelSwitch']",
        translations: {
            [Languages.enGB]: {
                label: "Panel switch",
                content:
                    "Switch between the item details, related comments and attachments, and the item's history.",
            },
            [Languages.enUS]: {
                label: "Panel switch",
                content:
                    "Switch between the item details, related comments and attachments, and the item's history.",
            },
            [Languages.nlBE]: {
                label: "Paneelschakelaar",
                content:
                    "Schakel tussen de details van het item, gerelateerde opmerkingen en bijlagen, en de geschiedenis van het item.",
            },
            [Languages.frFR]: {
                label: "Sélecteur de panneau",
                content:
                    "Vous pouvez basculer entre les détails de l'élément, les commentaires et pièces jointes associés et l'historique de l'élément.",
            },
            [Languages.deDE]: {
                label: "Panel-Schalter",
                content:
                    "Wechseln Sie zwischen den Artikeldetails, den zugehörigen Kommentaren und Anhängen sowie der Historie des Artikels.",
            },
        },
    },
    {
        code: "FlowControlFooter",
        target: "#resourceFooter footer button:nth-child(1)",
        translations: {
            [Languages.enGB]: {
                label: "Flow controls",
                content:
                    "Navigate your process flow by transitioning from one item status to another.",
            },
            [Languages.enUS]: {
                label: "Flow controls",
                content:
                    "Navigate your process flow by transitioning from one item status to another.",
            },
            [Languages.nlBE]: {
                label: "Bediening processtroom",
                content:
                    "Navigeer uw processtroom door van de ene itemstatus naar de andere te gaan.",
            },
            [Languages.frFR]: {
                label: "Contrôle des flux",
                content:
                    "Naviguer dans votre processus en passant d'un statut d'élément à un autre.",
            },
            [Languages.deDE]: {
                label: "Flusssteuerungen",
                content:
                    "Navigieren Sie durch Ihren Prozessfluss, indem Sie von einem Elementstatus zu einem anderen übergehen.",
            },
        },
    },
    {
        code: "CollectionViewSwitch",
        target: "#changeViewButton",
        translations: {
            [Languages.enGB]: {
                label: "Collection view switch",
                content: "Change the view of the collection.",
            },
            [Languages.enUS]: {
                label: "Collection view switch",
                content: "Change the view of the collection.",
            },
            [Languages.nlBE]: {
                label: "Schakelaar voor collectieoverzicht",
                content: "Verander de weergave van de collectie.",
            },
            [Languages.frFR]: {
                label: "Sélecteur de vue de la collection",
                content: "Modifier l'affichage de la collection.",
            },
            [Languages.deDE]: {
                label: "Wechsel der Sammlungsansicht",
                content: "Wechseln Sie die Ansicht der Sammlung.",
            },
        },
    },
    {
        code: "BookmarkAction",
        target: "#HEADER_BOOKMARK_BUTTON",
        translations: {
            [Languages.enGB]: {
                label: `Bookmark action ${Symbols.BookmarkEmpty}`,
                content:
                    "Create a personal shortcut to the page. Easily access all your bookmarks from your Welcome page or the navigation panel.",
            },
            [Languages.enUS]: {
                label: `Bookmark action ${Symbols.BookmarkEmpty}`,
                content:
                    "Create a personal shortcut to the page. Easily access all your bookmarks from your Welcome page or the navigation panel.",
            },
            [Languages.nlBE]: {
                label: `Bladwijzeractie ${Symbols.BookmarkEmpty}`,
                content:
                    "Maak een persoonlijke snelkoppeling naar de pagina. Krijg gemakkelijke toegang tot al uw bladwijzers vanaf uw welkomstpagina of het navigatiepaneel.",
            },
            [Languages.frFR]: {
                label: `Action de favoris ${Symbols.BookmarkEmpty}`,
                content:
                    "Créer un raccourci personnel vers la page. Accéder facilement à tous vos signets à partir de votre page d'accueil ou du panneau de navigation.",
            },
            [Languages.deDE]: {
                label: `Lesezeichen-Aktion ${Symbols.BookmarkEmpty}`,
                content:
                    "Erstellen Sie einen persönlichen Shortcut zur Seite. Greifen Sie einfach auf alle Ihre Lesezeichen von Ihrer Willkommensseite oder der Navigationsleiste aus zu.",
            },
        },
    },
    {
        code: "ShareAction",
        target: "#shareButton",
        translations: {
            [Languages.enGB]: {
                label: `Share action ${Symbols.CopyToClipboard}`,
                content:
                    "Copy the title and the URL of the page you are on to your clipboard.",
            },
            [Languages.nlBE]: {
                label: "Deelactie",
                content:
                    "Kopieer de titel en de URL van de pagina waarop u zich bevindt naar uw klembord.",
            },
            [Languages.frFR]: {
                label: "Action de partage",
                content:
                    "Copier le titre et le lien de cette page dans le presse-papiers.",
            },
            [Languages.deDE]: {
                label: "Teilen-aktion",
                content:
                    "Kopieren Sie den Titel und die URL der Seite, auf der Sie sich befinden, in Ihre Zwischenablage.",
            },
        },
    },
    {
        code: "ContextMenu",
        target: "#optionalHeaderButtonsButton",
        translations: {
            [Languages.enGB]: {
                label: `Context menu ${Symbols.EllipsisVertical}`,
                content:
                    "Access additional actions associated with the current page.",
            },
            [Languages.enUS]: {
                label: `Context menu ${Symbols.EllipsisVertical}`,
                content:
                    "Access additional actions associated with the current page.",
            },
            [Languages.nlBE]: {
                label: `Contextmenu ${Symbols.EllipsisVertical}`,
                content:
                    "Toegang tot extra acties in verband met de huidige pagina.",
            },
            [Languages.frFR]: {
                label: `Menu contextuel ${Symbols.EllipsisVertical}`,
                content:
                    "Accéder à des actions supplémentaires associées à la page en cours.",
            },
            [Languages.deDE]: {
                label: `Kontextmenü ${Symbols.EllipsisVertical}`,
                content:
                    "Zugriff auf zusätzliche Aktionen im Zusammenhang mit der aktuellen Seite.",
            },
        },
    },
    {
        code: "CollectionFilterToggle",
        target: "#datalist_filter_button, #itemdetailpage .column .childrenFilter",
        translations: {
            [Languages.enGB]: {
                label: "Collection filter toggle",
                content:
                    "Show or hide (and reset) available collection filters.",
            },
            [Languages.enUS]: {
                label: "Collection filter toggle",
                content:
                    "Show or hide (and reset) available collection filters.",
            },
            [Languages.nlBE]: {
                label: "Collectie-filterschakelaar",
                content:
                    "Toon of verberg (en reset) beschikbare collectie-filters.",
            },
            [Languages.frFR]: {
                label: "Filtre à bascule de la collection",
                content:
                    "Afficher ou masquer (et réinitialiser) les filtres de collection disponibles.",
            },
            [Languages.deDE]: {
                label: "Kollektionsfilter umschaltbar",
                content:
                    "Zeigt oder verbirgt (und setzt zurück) die verfügbaren Sammlungsfilter.",
            },
        },
    },
    {
        code: "ColumnSelector",
        target: "[data-column-selector-button]",
        translations: {
            [Languages.enGB]: {
                label: "Column selector",
                content:
                    "Manage the visibility and order of columns in the collection's table views.",
            },
            [Languages.enUS]: {
                label: "Column selector",
                content:
                    "Manage the visibility and order of columns in the collection's table views.",
            },
            [Languages.nlBE]: {
                label: "Kolomselector",
                content:
                    "Pas de zichtbaarheid en volgorde van kolommen in de tabelweergaven van de collectie aan.",
            },
            [Languages.frFR]: {
                label: "Sélecteur de colonne",
                content:
                    "Gérer la visibilité et l'ordre des colonnes pour les visualisations tabulaires de la collection.",
            },
            [Languages.deDE]: {
                label: "Spaltenselektor",
                content:
                    "Verwalten Sie die Sichtbarkeit und Reihenfolge der Spalten in den Tabellenansichten der Sammlung.",
            },
        },
    },
    /*{ // @Konstantin: This seems redundant
        code: `ItemPage`,
        target: undefined,
        translations: {
            [Languages.English]: {
                label: `Item page`,
                content:
                    `See an item's title and details.`,
            },
        },
    },*/
    {
        code: "WorkspaceCoverPage",
        target: ".cover-page-detail",
        translations: {
            [Languages.enGB]: {
                label: "Workspace cover page",
                content:
                    "Get an overview of all available workspace content, relevant indicators, and related bookmarks and shortcuts.",
            },
            [Languages.enUS]: {
                label: "Workspace cover page",
                content:
                    "Get an overview of all available workspace content, relevant indicators, and related bookmarks and shortcuts.",
            },
            [Languages.nlBE]: {
                label: "Coverpagina van de workspace",
                content:
                    "Krijg een overzicht van alle beschikbare inhoud van de workspace, relevante indicatoren, en gerelateerde bladwijzers en snelkoppelingen.",
            },
            [Languages.frFR]: {
                label: "Page de garde de l'espace de travail",
                content:
                    "Obtenir une vue d'ensemble du contenu de l'espace de travail disponible, des indicateurs pertinents et des favoris et raccourcis associés.",
            },
            [Languages.deDE]: {
                label: "Arbeitsbereich-Deckblatt",
                content:
                    "Verschaffen Sie sich einen Überblick über alle verfügbaren Arbeitsbereichsinhalte, relevante Indikatoren und zugehörige Lesezeichen und Verknüpfungen.",
            },
        },
    },
    /*{ // @Konstantin: Again, not sure this one makes sense
        code: `Collection page`,
        target: `#datalist-wrapper`, // TODO: Define a better identifier?
        translations: {
            `en-GB`: {
                label: `Collection page`,
                content: `Provides an overview of all items in a stand-alone collection.`,
            },
        },
    },*/
    {
        code: "WorkspacesWidget",
        target: "#newui-welcome [data-ft-content-widget='true']",
        translations: {
            [Languages.enGB]: {
                label: "Workspaces widget",
                content: "Get an overview of all available workspaces.",
            },
            [Languages.enUS]: {
                label: "Workspaces widget",
                content: "Get an overview of all available workspaces.",
            },
            [Languages.nlBE]: {
                label: "Workspace-widget",
                content: "Krijg een overzicht van alle beschikbare workspaces.",
            },
            [Languages.frFR]: {
                label: "Widget d'espaces de travail",
                content:
                    "Obtenir une vue d'ensemble de tous les espaces de travail disponibles.",
            },
            [Languages.deDE]: {
                label: "Workspaces-Widget",
                content:
                    "Verschafft einen Überblick über alle verfügbaren Workspaces.",
            },
        },
    },
    {
        code: "ContentWidget",
        target: "#newui-workspace-cover-page [data-ft-content-widget='true']",
        translations: {
            [Languages.enGB]: {
                label: "Content widget",
                content:
                    "Get an overview of the available content in the workspace.",
            },
            [Languages.enUS]: {
                label: "Content widget",
                content:
                    "Get an overview of the available content in the workspace.",
            },
            [Languages.nlBE]: {
                label: "Inhoud-widget",
                content:
                    "Krijg een overzicht van de beschikbare inhoud in de workspace.",
            },
            [Languages.frFR]: {
                label: "Widget de contenu",
                content:
                    "Obtenir une vue d'ensemble du contenu disponible dans l'espace de travail.",
            },
            [Languages.deDE]: {
                label: "Inhalts-Widget",
                content:
                    "Übersicht über die verfügbaren Inhalte im Arbeitsbereich.",
            },
        },
    },
    {
        code: "WidgetGridCustomization",
        target: "#gridConfigurationButton",
        translations: {
            [Languages.enGB]: {
                label: `Widget grid customization ${Symbols.WorkspaceGridSettings}`,
                content:
                    "Manage the widgets you would like to see on the page.",
            },
            [Languages.enUS]: {
                label: `Widget grid customization ${Symbols.WorkspaceGridSettings}`,
                content:
                    "Manage the widgets you would like to see on the page.",
            },
            [Languages.nlBE]: {
                label: `Widget-grid aanpassen ${Symbols.WorkspaceGridSettings}`,
                content: "Beheer de widgets die u op de pagina wilt zien.",
            },
            [Languages.frFR]: {
                label: `Personnalisation de la grille de widgets ${Symbols.WorkspaceGridSettings}`,
                content: "Gérer les widgets que vous désirez voir sur la page.",
            },
            [Languages.deDE]: {
                label: `Widget-Grid-Anpassung ${Symbols.WorkspaceGridSettings}`,
                content:
                    "Verwalten Sie die Widgets, die Sie auf der Seite sehen möchten.",
            },
        },
    },
    /* { // TODO: We don't have a way to distinguish between child and related item collection
         code: `ChildCollection`,
         target: undefined,
         translations: {
             [Languages.English]: {
                 label: `Child collection`,
                 content:
                     `Shows all children of this item.`,
             },
         },
     },
     { // TODO: We don't have a way to distinguish between child and related item collection
         code: `RelatedItemsCollection`,
         target: undefined,
         translations: {
             [Languages.English]: {
                 label: `Related items collection`,
                 content:
                     `Relations between different items are created in the item detail. Each relation between two items has a specific type, called the item relation type.`,
             },
         },
     },
     { // @Konstantin: This one is only available on hover
         code: `CollectionItemContextMenu`,
         target: `#contextMenuButton`,
         translations: {
             [Languages.English]: {
                 label: `Collection item context menu`,
                 content:
                     `Access an item's context menu directly from its collection view.`,
             },
         },
     },*/
    {
        code: "Tabs",
        target: "#tabs a",
        translations: {
            [Languages.enGB]: {
                label: "Tabs",
                content: "Navigate between groups of related content.",
            },
            [Languages.enUS]: {
                label: "Tabs",
                content: "Navigate between groups of related content.",
            },
            [Languages.nlBE]: {
                label: "Tabbladen",
                content: "Navigeer tussen groepen verwante inhoud.",
            },
            [Languages.frFR]: {
                label: "Onglets",
                content: "Naviguer entre les groupes de contenus liés.",
            },
            [Languages.deDE]: {
                label: "Tabs",
                content:
                    "Navigieren Sie zwischen Gruppen von verwandten Inhalten.",
            },
        },
    },
    {
        code: "TableExpandCollapse",
        target: "[data-ft-children-for-list] .header .collapsedall, [data-ft-children-for-list] .header .expandedall",
        translations: {
            [Languages.enGB]: {
                label: "Expand/collapse action",
                content:
                    "Expand or collapse the hierarchy in the collection to show or hide items' children.",
            },
            [Languages.enUS]: {
                label: "Expand/collapse action",
                content:
                    "Expand or collapse the hierarchy in the collection to show or hide items' children.",
            },
            [Languages.nlBE]: {
                label: "Uitvouw- en inklapactie",
                content:
                    "Vouw de hiërarchie in de verzameling uit of in om de kinderen van de items te tonen of te verbergen.",
            },
            [Languages.frFR]: {
                label: "Action de développement / réduction",
                content:
                    "Développer ou réduire la hiérarchie de la collection pour afficher ou masquer les enfants des éléments.",
            },
            [Languages.deDE]: {
                label: "Aktion aufklappen/zusammenklappen",
                content:
                    "Erweitern oder reduzieren Sie die Hierarchie in der Sammlung, um die untergeordneten Elemente ein- oder auszublenden.",
            },
        },
    },
    {
        code: "CollectionSettings",
        target: undefined,
        translations: {
            [Languages.enGB]: {
                label: `Collection settings ${Symbols.Configure}`,
                content:
                    "Change the settings of the collection: its sources, defining filters, and view.",
            },
            [Languages.enUS]: {
                label: `Collection settings ${Symbols.Configure}`,
                content:
                    "Change the settings of the collection: its sources, defining filters, and view.",
            },
            [Languages.nlBE]: {
                label: "Collectie-instellingen",
                content:
                    "Wijzig de instellingen van de collectie: de bronnen, filters definiëren en weergave.",
            },
            [Languages.frFR]: {
                label: "Paramètres de la collection",
                content:
                    "Modifier les paramètres de la collection : ses sources, la définition des filtres et l'affichage.",
            },
            [Languages.deDE]: {
                label: "Einstellungen der Kollektion",
                content:
                    "Ändern Sie die Einstellungen der Sammlung: ihre Quellen, die Definition von Filtern und die Ansicht.",
            },
        },
    },
    {
        code: "ConceptSettings",
        target: undefined,
        translations: {
            [Languages.enGB]: {
                label: `Concept settings ${Symbols.Configure}`,
                content:
                    "Change the settings of the concept: its fields, statuses, scripts, alerts, and more.",
            },
            [Languages.nlBE]: {
                label: "Conceptinstellingen",
                content:
                    "Wijzig de instellingen van het concept: de velden, statussen, scripts, waarschuwingen en meer.",
            },
            [Languages.frFR]: {
                label: "Paramètres du concept",
                content:
                    "Modifier les paramètres du concept : ses champs, ses statuts, ses scripts, ses alertes, etc.",
            },
            [Languages.deDE]: {
                label: "Konzept-Einstellungen",
                content:
                    "Ändern Sie die Einstellungen des Konzepts: seine Felder, Status, Skripte, Warnungen und mehr.",
            },
        },
    },
    {
        code: "FieldConfigurationGrid",
        target: ".item-detail.customize #contentcontainer",
        translations: {
            [Languages.enGB]: {
                label: "Field grid",
                content:
                    "Control the default configuration (settings and positioning) of the fields in all items of the concept.",
            },
            [Languages.enUS]: {
                label: "Field grid",
                content:
                    "Control the default configuration (settings and positioning) of the fields in all items of the concept.",
            },
            [Languages.nlBE]: {
                label: "Veldraster",
                content:
                    "Regel de standaardconfiguratie (instellingen en positionering) van de velden in alle items van het concept.",
            },
            [Languages.frFR]: {
                label: "Grille des champs",
                content:
                    "Contrôler la configuration par défaut (paramètres et positionnement) des champs dans tous les éléments du concept.",
            },
            [Languages.deDE]: {
                label: "Feldraster",
                content:
                    "Steuerung der Standardkonfiguration (Einstellungen und Positionierung) der Felder in allen Elementen des Konzepts.",
            },
        },
    },
    {
        code: "ConfigureFieldsPage",
        target: undefined,
        translations: {
            [Languages.enGB]: {
                label: `Standard action selector ${Symbols.Configure}`,
                content:
                    "Select which standard actions are available for the items of the concept.",
            },
            [Languages.nlBE]: {
                label: "Standaard actieselectie",
                content:
                    "Selecteer welke standaardacties beschikbaar zijn voor de items van het concept.",
            },
            [Languages.frFR]: {
                label: "Sélection de l'action standard",
                content:
                    "Sélectionnez les actions standard disponibles pour les éléments du concept.",
            },
            [Languages.deDE]: {
                label: "Auswahl der Standardaktion",
                content:
                    "Wählen Sie aus, welche Standardaktionen für die Elemente des Konzepts verfügbar sind.",
            },
        },
    },
    {
        code: "ConfigureFieldsToolbox",
        target: ".item-detail.customize .sdb-wrapper",
        translations: {
            [Languages.enGB]: {
                label: "Toolbox",
                content:
                    "Add new fields, relations, and tabs to the concept. To add a field or a relation, simply drag the desired attribute to an available slot in the field grid. To add a new tab, click Create new tab. ",
            },
            [Languages.nlBE]: {
                label: "Toolbox",
                content:
                    "Nieuwe velden, relaties en tabbladen toevoegen aan het concept. Om een veld of relatie toe te voegen, sleep je het gewenste attribuut naar een beschikbare sleuf in het veldraster. Om een nieuw tabblad toe te voegen, klik je op Nieuw tabblad maken.",
            },
            [Languages.frFR]: {
                label: "Boîte à outils",
                content:
                    "Ajouter de nouveaux champs, relations et onglets au concept. Pour ajouter un champ ou une relation, il suffit de faire glisser l'attribut souhaité vers un emplacement disponible dans la grille des champs. Pour ajouter un nouvel onglet, cliquez sur Créer un nouvel onglet.",
            },
            [Languages.deDE]: {
                label: "Werkzeugkiste",
                content:
                    "Fügen Sie dem Konzept neue Felder, Beziehungen und Registerkarten hinzu. Um ein Feld oder eine Beziehung hinzuzufügen, ziehen Sie das gewünschte Attribut einfach auf einen freien Platz im Feldraster. Um eine neue Registerkarte hinzuzufügen, klicken Sie auf Neue Registerkarte erstellen.",
            },
        },
    },
    {
        code: "ConfigureFieldsTabs",
        target: ".item-detail.customize .tid-sidebar",
        translations: {
            [Languages.enGB]: {
                label: "Tabs configuration",
                content:
                    "Manage the configured tabs of your concept. The positioning and the labels of the standard tabs cannot be modified. Custom tabs can be both moved and renamed.",
            },
            [Languages.enUS]: {
                label: "Tabs configuration",
                content:
                    "Manage the configured tabs of your concept. The positioning and the labels of the standard tabs cannot be modified. Custom tabs can be both moved and renamed.",
            },
            [Languages.nlBE]: {
                label: "Tabbladen-configuratie",
                content:
                    "Beheer de geconfigureerde tabbladen van uw concept. De positionering en de labels van de standaard tabbladen kunnen niet worden gewijzigd. Aangepaste tabbladen kunnen zowel verplaatst als hernoemd worden.",
            },
            [Languages.frFR]: {
                label: "Configuration des onglets",
                content:
                    "Gérer les onglets configurés de votre concept. Le positionnement et les étiquettes des onglets standard ne peuvent pas être modifiés. Les onglets personnalisés peuvent être déplacés et renommés.",
            },
            [Languages.deDE]: {
                label: "Konfiguration der Tabs",
                content:
                    "Verwalten Sie die konfigurierten Tabs Ihres Konzepts. Die Positionierung und die Beschriftung der Standard-Tabs können nicht verändert werden. Benutzerdefinierte Tabs können sowohl verschoben als auch umbenannt werden.",
            },
        },
    },
    {
        code: "SignatureField",
        target: "[data-ft-action='open-signatures']",
        translations: {
            [Languages.enGB]: {
                label: "Signature field",
                content: "See the item's digital signature.",
            },
            [Languages.enUS]: {
                label: "Signature field",
                content: "See the item's digital signature.",
            },
            [Languages.nlBE]: {
                label: "Handtekening-veld",
                content: "Bekijk de digitale handtekening van het item.",
            },
            [Languages.frFR]: {
                label: "Champ 'Signature'",
                content: "Voir la signature numérique de l'élément.",
            },
            [Languages.deDE]: {
                label: "Signatur-Feld",
                content: "Zeigt die digitale Signatur des Artikels an.",
            },
        },
    },
    {
        code: "TagsField",
        target: ".tag, #customizeTags",
        translations: {
            [Languages.enGB]: {
                label: "Tags field",
                content:
                    "Semantically annotate items with color-coded tags for easier classification and retrieval.",
            },
            [Languages.enUS]: {
                label: "Tags field",
                content:
                    "Semantically annotate items with color-coded tags for easier classification and retrieval.",
            },
            [Languages.nlBE]: {
                label: "Tags-veld",
                content:
                    "Semantisch annoteren van items met kleurgecodeerde tags voor gemakkelijker classificeren en terugvinden.",
            },
            [Languages.frFR]: {
                label: "Champ 'Tags'",
                content:
                    "Annoter sémantiquement les éléments avec des étiquettes codées en couleur pour faciliter la classification et la recherche.",
            },
            [Languages.deDE]: {
                label: "Tags-Feld",
                content:
                    "Annoter sémantiquement les éléments avec des étiquettes codées en couleur pour faciliter la classification et la recherche.",
            },
        },
    },
    {
        code: "ItemPageFooterMetadata",
        target: ".tid-footer [data-ft-field-label] span:nth-child(2)",
        translations: {
            [Languages.enGB]: {
                label: "Item metadata",
                content:
                    "See when an item was created, when it was last updated, and by whom.",
            },
            [Languages.enUS]: {
                label: "Item metadata",
                content:
                    "See when an item was created, when it was last updated, and by whom.",
            },
            [Languages.nlBE]: {
                label: "Item metadata",
                content:
                    "Bekijk wanneer een item is aangemaakt, wanneer het voor het laatst is bijgewerkt en door wie.",
            },
            [Languages.frFR]: {
                label: "Métadonnées de l'élément",
                content:
                    "Voir quand un élément a été créé, quand il a été mis à jour pour la dernière fois et par qui.",
            },
            [Languages.deDE]: {
                label: "Element-Metadaten",
                content:
                    "Sehen Sie, wann ein Artikel erstellt wurde, wann er zuletzt aktualisiert wurde und von wem.",
            },
        },
    },
    {
        code: "CollectionFilterAction",
        target: "#itemdetailpage .tid-content .filter .column .search, #datalist-wrapper .filter .column .search",
        translations: {
            [Languages.enGB]: {
                label: `Collection filter action ${Symbols.Filter}`,
                content: "Apply your choice of configured collection filters.",
            },
            [Languages.enUS]: {
                label: `Collection filter action ${Symbols.Filter}`,
                content: "Apply your choice of configured collection filters.",
            },
            [Languages.nlBE]: {
                label: `Collectie-filteractie ${Symbols.Filter}`,
                content:
                    "Pas uw keuze van geconfigureerde collectie-filters toe.",
            },
            [Languages.frFR]: {
                label: `Action de filtrage de collection ${Symbols.Filter}`,
                content: "Action de filtrage de collection",
            },
            [Languages.deDE]: {
                label: `Sammlungsfilter-Aktion ${Symbols.Filter}`,
                content:
                    "Wendet Ihre Auswahl an konfigurierten Sammlungsfiltern an.",
            },
        },
    },
    {
        code: "NewCommentNotify",
        target: ".comments-notify-wrapper",
        translations: {
            [Languages.enGB]: {
                label: "Notify users",
                content:
                    "Notify users to draw their attention to the conversation. The selected users will receive a notification with a link to the related item.",
            },
            [Languages.enUS]: {
                label: "Notify users",
                content:
                    "Notify users to draw their attention to the conversation. The selected users will receive a notification with a link to the related item.",
            },
            [Languages.nlBE]: {
                label: "Waarschuw gebruikers",
                content:
                    "Breng gebruikers op de hoogte om hun aandacht te vestigen op het gesprek. De geselecteerde gebruikers zullen een ",
            },
            [Languages.frFR]: {
                label: "Notifier les utilisateurs",
                content:
                    "Notifier les utilisateurs pour attirer leur attention sur la conversation. Les utilisateurs sélectionnés recevront une notification avec un lien vers l'élément concerné.",
            },
            [Languages.deDE]: {
                label: "Benutzer benachrichtigen",
                content:
                    "Benutzer benachrichtigen, um sie auf die Konversation aufmerksam zu machen. Die ausgewählten Benutzer",
            },
        },
    },
    {
        code: "CollectionPagination",
        target: "#itemdetailpage .tid-content .footer .footer-bottom-middle .paging li a, #datalist-wrapper .footer .footer-bottom-middle .paging li a",
        translations: {
            [Languages.enGB]: {
                label: "Pagination",
                content:
                    "See the number of pages of items in a collection and navigate between them.",
            },
            [Languages.enUS]: {
                label: "Pagination",
                content:
                    "See the number of pages of items in a collection and navigate between them.",
            },
            [Languages.nlBE]: {
                label: "Paginering",
                content:
                    "Bekijk het aantal pagina's van items in een collectie en navigeer ertussen.",
            },
            [Languages.frFR]: {
                label: "Pagination",
                content:
                    "Voir le nombre de pages des éléments d'une collection et naviguer entre eux.",
            },
            [Languages.deDE]: {
                label: "Paginierung",
                content:
                    "Sehen Sie die Anzahl der Seiten der Elemente in einer Sammlung und navigieren Sie zwischen ihnen.",
            },
        },
    },
    {
        code: "CollectionNumberOfItems",
        target: "#itemdetailpage .tid-content .footer .itemsperpage li:last-child span, #datalist-wrapper .footer .itemsperpage li:last-child span",
        translations: {
            [Languages.enGB]: {
                label: "Number of items per page",
                content:
                    "Choose how many items to display in the collection view.",
            },
            [Languages.enUS]: {
                label: "Number of items per page",
                content:
                    "Choose how many items to display in the collection view.",
            },
            [Languages.nlBE]: {
                label: "Aantal items per pagina",
                content:
                    "Kies hoeveel items moeten worden weergegeven in de collectieweergave.",
            },
            [Languages.frFR]: {
                label: "Nombre d'éléments par page",
                content:
                    "Choisir le nombre d'éléments à afficher dans la vue de la collection.",
            },
            [Languages.deDE]: {
                label: "Anzahl der Artikel pro Seite",
                content:
                    "Wählen Sie, wie viele Artikel in der Sammlungsansicht angezeigt werden sollen.",
            },
        },
    },
    {
        code: "LateralNavigationItemPage",
        target: "#newui-lateralnavigation a, #newui-lateralnavigation button",
        translations: {
            [Languages.enGB]: {
                label: `Lateral navigation between items ${Symbols.ArrowLeft} ${Symbols.ArrowRight}`,
                content:
                    "Navigate to the previous or the next item of the collection.",
            },
            [Languages.enUS]: {
                label: `Lateral navigation between items ${Symbols.ArrowLeft} ${Symbols.ArrowRight}`,
                content:
                    "Navigate to the previous or the next item of the collection.",
            },
            [Languages.nlBE]: {
                label: `Laterale navigatie tussen items ${Symbols.ArrowLeft} ${Symbols.ArrowRight}`,
                content:
                    "Navigeer naar het vorige of volgende item van de collectie.",
            },
            [Languages.frFR]: {
                label: `Navigation latérale entre les éléments ${Symbols.ArrowLeft} ${Symbols.ArrowRight}`,
                content:
                    "Naviguer vers l'élément précédent ou suivant de la collection.",
            },
            [Languages.deDE]: {
                label: `Seitliche Navigation zwischen Elementen ${Symbols.ArrowLeft} ${Symbols.ArrowRight}`,
                content:
                    "Navigieren Sie zum vorherigen oder nächsten Element der Sammlung.",
            },
        },
    },
    {
        code: "HomePage",
        target: "#goToHomePage",
        translations: {
            [Languages.enGB]: {
                label: `Home page ${Symbols.Home}`,
                content:
                    "The first page you see when you open the application is the home page. You can customize it in your profile settings.",
            },
            [Languages.enUS]: {
                label: `Home page ${Symbols.Home}`,
                content:
                    "The first page you see when you open the application is the home page. You can customize it in your profile settings.",
            },
            [Languages.nlBE]: {
                label: `Startpagina ${Symbols.Home}`,
                content:
                    "De eerste pagina die je ziet als je de toepassing opent is de startpagina. U kunt deze aanpassen in uw profielinstellingen.",
            },
            [Languages.frFR]: {
                label: `Page d'accueil ${Symbols.Home}`,
                content:
                    "La première page que vous voyez lorsque vous ouvrez l'application est la page d'accueil. Vous pouvez la personnaliser dans les paramètres de votre profil.",
            },
            [Languages.deDE]: {
                label: `Startseite ${Symbols.Home}`,
                content:
                    "Die erste Seite, die Sie sehen, wenn Sie die Anwendung öffnen, ist die Startseite. Sie können sie in Ihren Profileinstellungen anpassen.",
            },
        },
    },
]

const terms: Models.ContextualHelpTerm[] = rawTerms.map(
    ({ code, target, translations }) => {
        const { label, content } =
            translations[userLanguage] || translations[defaultLanguage]
        return {
            code,
            target,
            label,
            content,
        }
    },
)

export const dictionary: Models.ContextualHelpDictionary = {
    code: "defaultDictionary",
    isReadOnly: true,
    label: "default dictionary",
    terms,
}
